import React from "react";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import HygienicTransportationStore from "./store/HygieneStore";
import { formatDateTimeWithoutSec, formattedDate } from "../../../../Common/helper/helpar";

export const HygieneTransportationExport = ({ fromDate, toDate }) => {
  const { hygienicReportData } = HygienicTransportationStore();
  const storedFilters = localStorage.getItem("hygieneTransportationFilters");
  const parsedFilters = storedFilters ? JSON.parse(storedFilters) : {};

  /* ================= REMOVE BLANK ROWS ================= */
  const filteredData = React.useMemo(() => {
    return (hygienicReportData || []).filter((row) => {
      const hasValidDate = row.Date && row.Date !== "-";
      const hasTrips =
        Number(row.TotalHygienicVehicleTrips || 0) > 0 ||
        Number(row.TotalTrips || 0) > 0;

      return hasValidDate && hasTrips;
    });
  }, [hygienicReportData]);

  /* ================= TOTAL ROW ================= */
  const totalRow =
    filteredData.length > 0
      ? {
          Date: "Total",
          TotalHygienicVehicleTrips: filteredData.reduce(
            (sum, row) => sum + Number(row.TotalHygienicVehicleTrips || 0),
            0,
          ),
          TotalTrips: filteredData.reduce(
            (sum, row) => sum + Number(row.TotalTrips || 0),
            0,
          ),
        }
      : null;

  const exportRows = totalRow ? [...filteredData, totalRow] : filteredData;

  /* ================= EXCEL DOWNLOAD ================= */
  const downloadAsExcel = async () => {
    if (!exportRows.length) {
      alert("No data available to export.");
      return;
    }

    const headerText = `Hygienic Transportation Report as on (${fromDate} to ${toDate})`;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Hygienic Transportation Report");

    const boldFont = { bold: true };

    /* ===== TITLE ===== */

    const currentDate = new Date();
    const formattedDateStr = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
    const downloadDateTime = `Hygienic Transportation Report Generated on: ${formattedDateStr}, ${formattedTime}`;

    worksheet.addRow(["", "", downloadDateTime, ""]);
    worksheet.mergeCells(1, 2, 1, 8);
    worksheet.getCell(1, 2).value = downloadDateTime;
    worksheet.getCell(1, 2).font = boldFont;
    worksheet.getCell(1, 2).alignment = { horizontal: "center" };

    // Add report title
    worksheet.addRow(["", "", headerText, ""]);
    worksheet.mergeCells(2, 2, 2, 8);
    worksheet.getCell(2, 2).value = headerText;
    worksheet.getCell(2, 2).font = boldFont;
    worksheet.getCell(2, 2).alignment = { horizontal: "center" };

    /* ===== HEADER ===== */
    const headerRow = worksheet.addRow([
      "S.No",
      "Date",
      "Total number of vehicle Trips in Hygienic condition from the TS/SCTP's",
      "Total number of vehicle Trips Hygienic condition deployed from the TS/SCTP's",
      "Performance Achieved",
      "Description",
    ]);

    headerRow.eachCell((cell) => {
      cell.font = boldFont;
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F6B1B1" },
      };
    });

    /* ===== DATA ROWS ===== */
    exportRows.forEach((row, index) => {
      const isTotal = row.Date === "Total";

      const dataRow = worksheet.addRow([
        isTotal ? "" : index + 1,
        isTotal ? "Total" : formattedDate(row.Date),
        row.TotalHygienicVehicleTrips ?? 0,
        row.TotalTrips ?? 0,
        isTotal ? "-" : (row.PerformanceAchievedPercentage ?? "-"),
        row.Description ?? "N/A",
      ]);

      dataRow.eachCell((cell) => {
        cell.alignment = { horizontal: "center", vertical: "middle" };
        if (isTotal) cell.font = boldFont;
      });
    });

    worksheet.columns = [
      { width: 8 },
      { width: 18 },
      { width: 45 },
      { width: 45 },
      { width: 22 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Hygienic_Transportation_Report.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ================= PDF DOWNLOAD ================= */
  const downloadAsPDF = () => {
    if (!exportRows.length) {
      alert("No data available to export.");
      return;
    }

    const doc = new jsPDF("l", "mm", "a4");

    const headerText = `Hygienic Transportation Report as on  (${fromDate} to ${toDate})`;

    const tableColumns = [
      "S.No",
      "Date",
      "Total number of vehicle Trips in Hygienic condition from the TS/SCTP's",
      "Total number of vehicle Trips Hygienic condition deployed from the TS/SCTP's",
      "Performance Achieved",
      "Description",
    ];

    const tableRows = exportRows.map((row, index) => {
      const isTotal = row.Date === "Total";
      return [
        isTotal ? "" : index + 1,
        isTotal ? "Total" : formattedDate(row.Date),
        row.TotalHygienicVehicleTrips ?? 0,
        row.TotalTrips ?? 0,
        isTotal ? "-" : (row.PerformanceAchievedPercentage ?? "-"),
        row.Description ?? 0,
      ];
    });

    doc.text(headerText, doc.internal.pageSize.width / 2, 15, {
      align: "center",
    });

    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: 25,
      styles: { halign: "center", valign: "middle" },
      headStyles: { halign: "center" },
      didParseCell: (data) => {
        if (data.row.raw[1] === "Total") {
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    doc.save("Hygienic_Transportation_Report.pdf");
  };

  return (
    <>
      <button className="btn btn-excel me-2" onClick={downloadAsPDF}>
        <i className="fa-solid fa-file-pdf me-1"></i> PDF
      </button>
      <button className="btn btn-excel" onClick={downloadAsExcel}>
        <i className="fa-solid fa-file-excel me-1"></i> Excel
      </button>
    </>
  );
};
